import { useCallback, useEffect, useMemo, useState, useSyncExternalStore } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import {
  Maximize2,
  Minimize2,
  RefreshCw,
  ShoppingBag,
  TriangleAlert,
  Undo2,
  UtensilsCrossed,
} from 'lucide-react';
import { OrderItemStatus, VENUE_EVENTS, type OrderItemEventPayload } from '@/lib/shared-types';
import { api } from '@/lib/api';
import { useRealtime, useRealtimeResync } from '@/Hooks/useRealtime';
import { onRealtimeAuthFailure, realtimeAuthFailure, reconnectRealtime } from '@/lib/realtime';
import { usePermission } from '@/Hooks/usePermission';
import { useAppDispatch, useAppSelector } from '@/Redux/store';
import {
  applyItemEvent,
  invalidateBoards,
  invalidateOrder,
  useKitchenBoardQuery,
  useKitchenTakeawayQueueQuery,
  useListStationsQuery,
  type KitchenBoardQuery,
  type TakeawayQueueQuery,
  type Ticket,
  type TicketItem,
} from '@/Redux/Slices/operationsApi';
import { cn } from '@/lib/utils';
import { Badge, toneForStatus } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog } from '@/components/ui/dialog';
import { Field, Select, Textarea } from '@/components/ui/field';
import { Icon } from '@/components/ui/icon';
import { EmptyState, ErrorState, LoadingState } from '@/components/ui/states';
import { KitchenItemActions, type ItemAction } from './KitchenItemActions';
import { UnroutedLane } from './UnroutedLane';
import { useStationMode } from '@/components/StationMode';
import {
  apiMessage,
  formatDuration,
  formatQuantity,
  formatTime,
  useBranches,
  useNow,
} from './shared';

const STATION_KEY = 'venue.kds.stationId';

/** Where station mode's own flag lives. Exported so the test names one key. */
export const KDS_STATION_KEY = 'venue.kds.focus';

/**
 * S-16 — the board used to ask for NEW/PREPARING/RECALLED and nothing else,
 * with READY behind an unticked checkbox.
 *
 * A ticket's status is derived from its items and `KITCHEN_DONE` counts READY,
 * AWAITING_PICKUP, PICKED_UP and SERVED alike, so a ticket stays READY for the
 * whole of the pass — which is exactly where the pickup and serve controls
 * live. Defaulting the board to the cooking lane hid every row those controls
 * act on, and the line dead-ended at READY with nowhere to click. `all` is the
 * default now: the station's work and its pass, on one board.
 */
const LANE_STATUSES = {
  cooking: 'NEW,PREPARING,RECALLED',
  pass: 'READY',
  all: 'NEW,PREPARING,RECALLED,READY',
} as const;

type Lane = keyof typeof LANE_STATUSES;
const LANES = Object.keys(LANE_STATUSES) as Lane[];

/**
 * S-41 — dine-in and takeaway used to be two TABS, and a ticket behind a tab
 * nobody switched to is a ticket that does not exist: nothing errors, nothing
 * warns, a drink simply sits unmade because the barista never clicked. Both
 * queries run unconditionally now and both sets of tickets land on ONE board.
 *
 * One merged board rather than two columns, because the person working it works
 * oldest-first: two columns would make them compare timers across columns to
 * find what is late. The cost of merging is that the row no longer tells you
 * which queue it came from by where it sits — so every ticket carries a badge.
 *
 * The marker cannot be inferred from the row. Both endpoints answer with the
 * same `Ticket`, which has no table on it at all: `takeawayStatus` and
 * `customerName` are optional and a dine-in ticket may well have a customer.
 * The one thing known for certain is which query answered, so the kind is
 * stamped on at merge time and never read back off a field.
 */
type BoardKind = 'DINE_IN' | 'TAKEAWAY';
type BoardTicket = Ticket & { boardKind: BoardKind };

/**
 * The order of one merged board has to be decided here — until now each board
 * arrived pre-sorted and the screen just painted whichever came back.
 *
 * Priority first, then oldest fired: the priority control on every card
 * promises that pressing it moves the ticket up, and a merge that sorted on age
 * alone would quietly break that promise. `createdAt` is the tie break the cook
 * actually works to; `elapsedSeconds` covers the row whose timestamp will not
 * parse (the same fallback the card's timer uses), and `id` is the final tie
 * break so two tickets fired in the same millisecond keep one fixed order
 * across renders instead of resolving on merge order.
 */
function compareTickets(a: BoardTicket, b: BoardTicket): number {
  if (a.priority !== b.priority) return b.priority - a.priority;
  const aFired = Date.parse(a.createdAt);
  const bFired = Date.parse(b.createdAt);
  if (!Number.isNaN(aFired) && !Number.isNaN(bFired) && aFired !== bFired) return aFired - bFired;
  if (a.elapsedSeconds !== b.elapsedSeconds) return b.elapsedSeconds - a.elapsedSeconds;
  return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
}

/*
 * `Station`, `Ticket` and `TicketItem` are declared in
 * `Redux/Slices/operationsApi` now. The board renders the cache entry directly,
 * so the shape the socket patches and the shape drawn here must be one type.
 */

/**
 * Nothing left to do here: every surviving line is in front of a guest.
 *
 * The service has no "closed" ticket state — a ticket whose items are all
 * SERVED still reads READY — so without this a finished ticket would sit on the
 * board for the rest of the day. Hidden, never dropped: the count and the way
 * back are on screen (§16).
 */
function isFinished(ticket: Ticket): boolean {
  const active = ticket.items.filter((item) => item.status !== OrderItemStatus.CANCELLED);
  return active.length > 0 && active.every((item) => item.status === OrderItemStatus.SERVED);
}

/**
 * `recallTicket()` refuses anything but a READY ticket, and refuses even that
 * once every line has been carried away — `RECALLABLE` in the service is
 * exactly READY plus AWAITING_PICKUP. A recall is the one backwards move in the
 * system, so it is the one place the board cannot read the answer off
 * `ORDER_ITEM_TRANSITIONS`: the edge deliberately does not exist there.
 */
const RECALLABLE_ITEM_STATUSES: string[] = [OrderItemStatus.READY, OrderItemStatus.AWAITING_PICKUP];

function recallBlockedReason(ticket: Ticket): 'notReady' | 'allPickedUp' | null {
  if (ticket.status !== 'READY') return 'notReady';
  const recallable = ticket.items.filter((item) => RECALLABLE_ITEM_STATUSES.includes(item.status));
  return recallable.length ? null : 'allPickedUp';
}

/** `TicketPriorityDto.priority` is capped at 100, and the button steps by 10. */
const MAX_TICKET_PRIORITY = 100;

/**
 * How long this ticket has been running, in the one place that decides it.
 *
 * The card drew its own timer and the station bar now counts breaches, and two
 * expressions of "is this one late" drift the moment one of them is touched —
 * a header that says three are over target above a board showing two is worse
 * than no header at all. `readyAt` stops the clock; a `createdAt` that will not
 * parse falls back to the server's own count, which is what the row carries it
 * for.
 */
function elapsedOf(ticket: Ticket, now: number): number {
  const started = new Date(ticket.createdAt).getTime();
  if (Number.isNaN(started)) return ticket.elapsedSeconds;
  const finished = ticket.readyAt ? new Date(ticket.readyAt).getTime() : now;
  return Math.max(0, Math.floor((finished - started) / 1000));
}

function isBreached(ticket: Ticket, now: number): boolean {
  return ticket.slaSeconds != null
    ? elapsedOf(ticket, now) > ticket.slaSeconds
    : ticket.isSlaBreached;
}

/**
 * The badge that replaces "which tab am I on".
 *
 * BOTH kinds are labelled, not only takeaway: on a merged board an unbadged
 * card would mean "dine-in" by absence, and absence is precisely the signal
 * that failed here. Sky for takeaway so a bag reads from across the kitchen.
 * The words are `order.fulfillment.*` — already translated, and already what
 * every other screen in the app calls these two things.
 */
function TicketKindBadge({ kind, count }: { kind: BoardKind; count?: number }) {
  const { t } = useTranslation();
  const Icon = kind === 'TAKEAWAY' ? ShoppingBag : UtensilsCrossed;
  return (
    <Badge tone={kind === 'TAKEAWAY' ? 'info' : 'neutral'} className="gap-1 whitespace-nowrap">
      <Icon className="h-3 w-3 shrink-0" aria-hidden="true" />
      {t(`order.fulfillment.${kind}`)}
      {count != null ? ` · ${count}` : ''}
    </Badge>
  );
}

/**
 * P8-01 → P8-06 — the kitchen display.
 *
 * Station picker first, because a KDS terminal belongs to one station. The
 * board then shows that station's tickets with a live timer against the
 * station's `slaSeconds`; a breach is highlighted rather than merely logged.
 *
 * S-41 — §8.1's two boards are two READS, not two screens. They were shown as
 * tabs and the tab nobody clicked hid live work behind it; they are merged into
 * one chronological board now, each ticket badged with the queue it came from.
 *
 * D-08 — Recall used to appear on every ticket the holder of `kitchen.recall`
 * could see. The default board filter is NEW/PREPARING/RECALLED and the service
 * only accepts a READY ticket, so on the screen as shipped the button was
 * guaranteed to fail on every row it was drawn on. It now mirrors both of the
 * service's guards and names the one that is blocking it.
 *
 * S-16 — the board used to stop at READY. The line does not: §15.2 runs on
 * through AWAITING_PICKUP, PICKED_UP and SERVED, `POST /kitchen/items/:id/
 * pickup` and `/serve` have always served those steps, and no screen called
 * them — which is why §32's pickup-delay report had no stamps to read. The
 * pass now finishes here too (`KitchenItemActions`), and the lane filter makes
 * the rows those controls act on reachable in the first place.
 */
export default function Kitchen() {
  const { t, i18n } = useTranslation();
  const { can } = usePermission();
  const branches = useBranches();
  const now = useNow(1000);
  const dispatch = useAppDispatch();

  const [branchId, setBranchId] = useState('');
  const [stationId, setStationId] = useState<string | null>(
    () => localStorage.getItem(STATION_KEY) || null,
  );
  const [lane, setLane] = useState<Lane>('all');
  const [showFinished, setShowFinished] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [recallTicket, setRecallTicket] = useState<Ticket | null>(null);
  const [recallReason, setRecallReason] = useState('');

  // Reference data in the strict sense: a station list changes when someone
  // edits the kitchen, never during service. Cached, so returning to the picker
  // — or reading the station's name in the header — costs nothing.
  const stationParams = useMemo(() => ({ branchId, limit: 100 }), [branchId]);
  const stations = useListStationsQuery(stationParams);
  const stationRows = stations.data?.rows ?? [];
  const stationsError = stations.error
    ? ((stations.error as { message?: string }).message ?? t('operations.errors.actionFailed'))
    : null;

  useEffect(() => {
    if (stationId) localStorage.setItem(STATION_KEY, stationId);
    else localStorage.removeItem(STATION_KEY);
  }, [stationId]);

  /**
   * Station mode — see `components/StationMode.tsx` for what it is and why it
   * is two mechanisms. Gated on there being a board at all: the picker is a
   * setup screen and the wrong place to be locked into a wall-display mode.
   *
   * The board draws its own overlay rather than taking `shellClassName`: the
   * station bar sticks to the scrollport, so the one thing this shell cannot
   * have is top padding.
   */
  const focus = useStationMode(KDS_STATION_KEY, { available: !!stationId });
  const focused = focus.active;

  const boardParams = useMemo<KitchenBoardQuery>(
    () => ({
      stationId: stationId ?? '',
      status: LANE_STATUSES[lane],
      fulfillmentType: 'DINE_IN',
      branchId,
      limit: 100,
    }),
    [stationId, lane, branchId],
  );
  // The counter still has to hand a takeaway bag over, so the same lane applies.
  // `TakeawayQueueQueryDto` has no `fulfillmentType` — the route is already
  // takeaway, and `forbidNonWhitelisted` would answer 400 rather than ignore it.
  const takeawayParams = useMemo<TakeawayQueueQuery>(
    () => ({
      stationId: stationId ?? '',
      status: LANE_STATUSES[lane],
      branchId,
      limit: 100,
    }),
    [stationId, lane, branchId],
  );

  /**
   * The rooms this board listens in, spelled out rather than defaulted.
   *
   * `useRealtime`'s default is `branch:<the branch in the header>`, and this is
   * the one screen where that is provably the wrong branch. The board is keyed
   * on a **station**, picked from a list that is filtered by this page's own
   * branch selector — which starts at "all branches" and is not the global one.
   * A cook on a station in branch B while the header still says branch A joined
   * `branch:A` and received nothing: six subscriptions, all live, all silent,
   * with a green pill and a board that only ever moved when the cook touched it.
   *
   * Two rooms, because the fan-out in `realtime.service.ts` is not uniform:
   *  - `station:` is the exact audience for the four item events
   *    (`onOrderItemEvent` targets branch + station), and it is right even when
   *    the branch below is not;
   *  - `branch:` is the ONLY staff room the order and takeaway events reach —
   *    `onOrderEvent` and `onTakeawayEvent` target branch/order/session/table
   *    and no station — so it cannot be dropped in favour of the station alone.
   *
   * The page's own branch filter wins when it is set, because that is the branch
   * whose tickets are actually on screen; the header's branch is the fallback for
   * the "all branches" default.
   */
  const activeBranchId = useAppSelector((state) => state.ui.activeBranchId);
  const boardRooms = useMemo(() => {
    const effectiveBranchId = branchId || activeBranchId;
    return [
      ...(stationId ? [`station:${stationId}`] : []),
      ...(effectiveBranchId ? [`branch:${effectiveBranchId}`] : []),
    ];
  }, [stationId, branchId, activeBranchId]);

  /**
   * `refetchOnMountOrArgChange` on purpose. Cached tickets are worth painting
   * the instant the board opens — but they are only worth *trusting* for the
   * moment it takes to revalidate, so every mount asks the server as well.
   * Together with the 30s `keepUnusedDataFor` on these two endpoints, a KDS that
   * has been away is never showing a board older than the request in flight.
   *
   * Both queries run, always. The `skip` that used to read `tab !== 'board'` is
   * the bug itself: the queue is not fetched, so it is not on screen, so nobody
   * makes it. Only the station gate survives — with no station picked there is
   * no board to ask for.
   */
  const dineInBoard = useKitchenBoardQuery(boardParams, {
    skip: !stationId,
    refetchOnMountOrArgChange: true,
  });
  const takeawayBoard = useKitchenTakeawayQueueQuery(takeawayParams, {
    skip: !stationId,
    refetchOnMountOrArgChange: true,
  });

  // One source of tickets: the two cache entries themselves. There is no local
  // mirror to fall out of step with them (see the realtime block below) — the
  // merge is derived on every render from whatever the caches currently hold,
  // which is what lets a socket patch reach the board without a request.
  const dineInRows = dineInBoard.data?.rows;
  const takeawayRows = takeawayBoard.data?.rows;
  const tickets = useMemo<BoardTicket[]>(() => {
    const merged: BoardTicket[] = [];
    const seen = new Set<string>();
    for (const [kind, rows] of [
      ['DINE_IN', dineInRows ?? []],
      ['TAKEAWAY', takeawayRows ?? []],
    ] as const) {
      for (const ticket of rows) {
        // The two reads are disjoint by construction (one filters
        // `fulfillmentType=DINE_IN`, the other is the takeaway route), but a
        // duplicate id would be a duplicate React key on a live board — cheap
        // insurance against a routing change upstream.
        if (seen.has(ticket.id)) continue;
        seen.add(ticket.id);
        merged.push({ ...ticket, boardKind: kind });
      }
    }
    return merged.sort(compareTickets);
  }, [dineInRows, takeawayRows]);

  /**
   * Two reads, so "loading", "empty" and "failed" are no longer one flag each.
   *
   * A spinner covers the board only while BOTH are on their first request:
   * whichever queue answers first is worth working. A failure is reported per
   * queue, in a banner beside the tickets rather than instead of them — the
   * takeaway read falling over must never blank the dine-in tickets, which is
   * the same "work you cannot see" failure the tabs caused.
   */
  const errorMessage = (error: unknown) =>
    error ? ((error as { message?: string }).message ?? t('operations.errors.actionFailed')) : null;

  const boardFailures = (
    [
      { kind: 'DINE_IN' as const, query: dineInBoard },
      { kind: 'TAKEAWAY' as const, query: takeawayBoard },
    ] as const
  ).flatMap(({ kind, query }) => {
    const message = errorMessage(query.error);
    return message ? [{ kind, message, retry: query.refetch }] : [];
  });

  const initialLoading = dineInBoard.isLoading && takeawayBoard.isLoading;
  const anyFetching = dineInBoard.isFetching || takeawayBoard.isFetching;

  const dineInRefetch = dineInBoard.refetch;
  const takeawayRefetch = takeawayBoard.refetch;
  const reload = useCallback(() => {
    void dineInRefetch();
    void takeawayRefetch();
  }, [dineInRefetch, takeawayRefetch]);

  // ------------------------------------------------------------- realtime

  /**
   * Every item event is written straight into the cache entry the board is
   * rendering — no request, so a new state is on the pass in the time a socket
   * frame takes, and no second copy of the tickets in local state for a later
   * refetch to quietly overwrite.
   *
   * `applyItemEvent` patches *every* cached board and order list, not just the
   * lane on screen: switching lane or station must not resurrect a status this
   * terminal already watched change.
   */
  const patchItem = useCallback(
    (payload: OrderItemEventPayload) => {
      dispatch(applyItemEvent(payload));
    },
    [dispatch],
  );

  const refreshBoards = useCallback(() => {
    dispatch(invalidateBoards());
  }, [dispatch]);

  useRealtime<OrderItemEventPayload>(VENUE_EVENTS.ITEM_PREPARING, patchItem, boardRooms);
  useRealtime<OrderItemEventPayload>(VENUE_EVENTS.ITEM_READY, patchItem, boardRooms);
  // §15.2 — the pass is a shared surface: the waiter app and this board drive
  // the same two transitions, so a line collected on a handheld has to stop
  // offering "Picked up" here without anyone pressing refresh.
  useRealtime<OrderItemEventPayload>(VENUE_EVENTS.ITEM_PICKED_UP, patchItem, boardRooms);
  useRealtime<OrderItemEventPayload>(VENUE_EVENTS.ITEM_SERVED, patchItem, boardRooms);
  // A newly fired order creates tickets that are on no board yet, and the
  // payload carries no ticket to patch in — the one case that costs a request.
  // It invalidates both boards, which is now literally both halves of the
  // screen: a takeaway bag fired while the cook is looking at the dine-in
  // tickets appears among them, in its place in the queue, with no interaction.
  useRealtime(VENUE_EVENTS.ORDER_SENT_TO_KITCHEN, refreshBoards, boardRooms);

  /**
   * §16 — the order-level half of a ticket's life, which this board was missing.
   *
   * `syncOrder` emits ORDER_READY the moment the last line of a dine-in order
   * goes READY. The item events that caused it are patched in above, but the
   * *ticket* row carries a derived status and a `readyAt` the payload has no
   * field for, so the row kept rendering as PREPARING on every terminal except
   * the one that pressed the button. Cheap: the board is already invalidated by
   * the same tag.
   */
  useRealtime(VENUE_EVENTS.ORDER_READY, refreshBoards, boardRooms);

  /**
   * All three takeaway transitions, not just the first.
   *
   * The takeaway queue is half this screen, and it was subscribed to
   * TAKEAWAY_RELEASED alone — the event that puts a bag *on* the board.
   * `takeaway.service.ts` maps every `TakeawayStatus` to an event and
   * `realtime.service.ts` fans all three to `branch:`, so READY and
   * HANDED_OVER were on the wire the whole time and this board ignored them: a
   * bag handed over at the counter stayed in the queue here until someone
   * pressed refresh, which is exactly the double-handover the queue exists to
   * prevent.
   */
  useRealtime(VENUE_EVENTS.TAKEAWAY_RELEASED, refreshBoards, boardRooms);
  useRealtime(VENUE_EVENTS.TAKEAWAY_READY, refreshBoards, boardRooms);
  useRealtime(VENUE_EVENTS.TAKEAWAY_HANDED_OVER, refreshBoards, boardRooms);

  /**
   * Come back from a blip with the truth, not with whatever was on screen when
   * the socket died.
   *
   * Everything above patches or invalidates on an event. None of it runs while
   * the socket is down, and nothing replays: a two-minute reconnect over a
   * service rush leaves this board showing a state that stopped being true two
   * minutes ago, still ticking its elapsed timers, with no spinner and no
   * warning. `reload()` is the same refetch the manual button fires.
   */
  useRealtimeResync(reload);

  /**
   * Realtime has given up, and the board says so instead of looking live.
   *
   * `connectRealtime` stops retrying after three consecutive refusals and says
   * so through `onAuthFailure`, which `lib/realtime.ts` forwards here. The point
   * is the one the package makes: a caller that ignores it gets the bug back —
   * a KDS sitting on stale tickets with nothing on screen to say why is the
   * failure this whole exercise is about.
   */
  const realtimeDown = useSyncExternalStore(
    onRealtimeAuthFailure,
    realtimeAuthFailure,
    realtimeAuthFailure,
  );

  // --------------------------------------------------------------- actions

  /**
   * The write itself is unchanged — the axios client, not a cache mutation:
   * `baseApi` is deliberately not a write path for orders. What it reports
   * afterwards is a tag, not a screen: the same order shown on the Orders board
   * repaints from the same invalidation, whether or not it is the screen that
   * fired the call.
   */
  const run = useCallback(
    async (
      key: string,
      request: () => Promise<unknown>,
      successKey: string,
      orderId: string,
    ): Promise<boolean> => {
      setBusy(key);
      try {
        await request();
        toast.success(t(successKey));
        dispatch(invalidateOrder(orderId));
        dispatch(invalidateBoards());
        return true;
      } catch (e) {
        toast.error(apiMessage(e, t('operations.errors.actionFailed')));
        return false;
      } finally {
        setBusy(null);
      }
    },
    [dispatch, t],
  );

  /**
   * §15.2 — one handler, two contracts. The station's steps are a status PATCH;
   * the pass has its own routes because it is the waiter's move and carries the
   * `pickedUpAt` / `servedAt` stamps §32 reports on. `POST /pickup` walks
   * READY → AWAITING_PICKUP → PICKED_UP server-side, so the board never has to
   * fire the intermediate call itself.
   */
  const runAction = (item: TicketItem, action: ItemAction) =>
    run(
      `item:${item.id}`,
      () =>
        action.lane === 'pass'
          ? api.post(`/kitchen/items/${item.id}/${action.id === 'SERVED' ? 'serve' : 'pickup'}`)
          : api.patch(`/kitchen/items/${item.id}`, { status: action.target }),
      `operations.kitchen.itemMoved.${action.id}`,
      item.orderId,
    );

  const raisePriority = (ticket: Ticket) =>
    run(
      `priority:${ticket.id}`,
      () =>
        api.patch(`/kitchen/tickets/${ticket.id}/priority`, {
          priority: Math.min(100, (ticket.priority ?? 0) + 10),
        }),
      'operations.kitchen.priorityUpdated',
      ticket.orderId,
    );

  const submitRecall = async () => {
    if (!recallTicket) return;
    const done = await run(
      `recall:${recallTicket.id}`,
      () =>
        api.post(`/kitchen/tickets/${recallTicket.id}/recall`, {
          reason: recallReason.trim() || undefined,
        }),
      'operations.kitchen.recalled',
      recallTicket.orderId,
    );
    if (done) {
      setRecallTicket(null);
      setRecallReason('');
    }
  };

  // ---------------------------------------------------------------- render

  const finishedTickets = tickets.filter(isFinished);
  const visibleTickets = showFinished ? tickets : tickets.filter((ticket) => !isFinished(ticket));

  // Not a filter and not a tab — a count. It says "there are two of these
  // waiting" without ever being a place the other kind can hide.
  const dineInCount = visibleTickets.filter((ticket) => ticket.boardKind === 'DINE_IN').length;
  const takeawayCount = visibleTickets.length - dineInCount;

  const breachedCount = visibleTickets.filter((ticket) => isBreached(ticket, now)).length;

  /**
   * The branch this board belongs to, spelled out only in station mode.
   *
   * The header's branch switcher is one of the things the mode takes away, and
   * a wall screen in a two-branch venue that names its station but not its
   * branch is a board two kitchens can each believe is theirs. The page's own
   * filter wins when it is set — it is what the tickets were fetched with — and
   * the header's branch is the fallback, exactly as `boardRooms` resolves it.
   */
  const branchName = useMemo(() => {
    const id = branchId || activeBranchId;
    return id ? (branches.find((branch) => branch.id === id)?.name ?? null) : null;
  }, [branchId, activeBranchId, branches]);

  /**
   * Seconds, not minutes. A wall board with a stopped clock is indistinguishable
   * from a wall board on a dead socket, and the ticking digit is the cheapest
   * proof there is that this screen is still being painted.
   */
  const clock = useMemo(
    () => new Intl.DateTimeFormat(i18n.language, { timeStyle: 'medium' }),
    [i18n.language],
  );

  /**
   * What the extra width buys.
   *
   * Dropping the rail and the page padding gives back about 300px on a 1080p
   * screen — roughly a fifth of the board. Spent on a fifth column it would buy
   * one more ticket at the size a cook already cannot read from the pass, so it
   * goes on type instead: the same four columns at 1920, each card 20% wider,
   * with the ticket number and the timer at a size that carries across a
   * kitchen. A fifth column arrives only at 2200px, where there is genuinely
   * room for both.
   */
  const dense = focused
    ? {
        // Arbitrary widths the whole way up, not `lg:`/`2xl:` with one arbitrary
        // step on the end: Tailwind emits arbitrary variants BEFORE the named
        // breakpoints, so a `min-[2200px]:` rule loses to a `2xl:` one on a
        // 2560px screen and the fifth column silently never arrives.
        grid: 'grid gap-3 grid-cols-2 min-[1100px]:grid-cols-3 min-[1700px]:grid-cols-4 min-[2200px]:grid-cols-5',
        title: 'text-3xl',
        timer: 'text-3xl',
        meta: 'text-base',
        item: 'text-lg',
        sub: 'text-sm',
        badge: 'text-sm',
        bar: 'h-2.5',
        control: 'px-4 py-2 text-base',
      }
    : {
        grid: 'grid gap-4 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4',
        title: '',
        timer: 'font-medium',
        meta: 'text-xs',
        item: 'text-sm',
        sub: 'text-xs',
        badge: '',
        bar: 'h-1.5',
        control: 'px-3 py-1.5 text-sm',
      };

  const liveRecallTicket = recallTicket
    ? (tickets.find((ticket) => ticket.id === recallTicket.id) ?? recallTicket)
    : null;
  const liveRecallBlocked = liveRecallTicket ? recallBlockedReason(liveRecallTicket) : null;

  if (!stationId) {
    return (
      <div className="space-y-4">
        <h1 className="text-2xl font-semibold">{t('nav.kitchen')}</h1>
        <p className="text-sm text-ink-muted">
          {t('operations.kitchen.pickStationHint')}
        </p>

        {branches.length > 0 && (
          <Card>
            <CardContent className="max-w-sm p-4">
              <Field label={t('operations.filters.branch')}>
                <Select value={branchId} onChange={(e) => setBranchId(e.target.value)}>
                  <option value="">{t('operations.filters.allBranches')}</option>
                  {branches.map((b) => (
                    <option key={b.id} value={b.id}>
                      {b.name}
                    </option>
                  ))}
                </Select>
              </Field>
            </CardContent>
          </Card>
        )}

        {/*
          S-32 — before the stations, because a line that reached no kitchen is
          not on any of them. A cook landing here to pick a station is the one
          moment they are guaranteed to be looking at this screen.
        */}
        <UnroutedLane branchId={branchId} stations={stationRows} />

        {stationsError ? (
          <ErrorState message={stationsError} onRetry={() => void stations.refetch()} />
        ) : stations.isLoading ? (
          <LoadingState label={t('common.loading')} />
        ) : !stationRows.length ? (
          <EmptyState
            title={t('operations.kitchen.noStations')}
            hint={t('operations.kitchen.noStationsHint')}
          />
        ) : (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {stationRows.map((s) => (
              <button
                key={s.id}
                onClick={() => setStationId(s.id)}
                className="rounded-xl border bg-surface p-5 text-start shadow-sm transition-colors hover:bg-hover"
              >
                <div className="text-base font-semibold">{s.name}</div>
                <div className="text-xs text-ink-muted">{s.code}</div>
                <div className="mt-2 text-xs text-ink-muted">
                  {s.slaSeconds
                    ? t('operations.kitchen.slaTarget', {
                        duration: formatDuration(s.slaSeconds),
                      })
                    : t('operations.kitchen.noSla')}
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    /*
      Station mode is drawn here rather than asked for from `Layout`: this page
      owns the board, and a fixed overlay covers the rail, the header and the
      page padding without a second screen having to know the mode exists.
      `overscroll-contain` so a flick at the end of a long board does not start
      scrolling whatever is underneath it.
    */
    <div
      ref={focus.ref}
      className={
        focused
          ? // No top padding: the sticky bar below sticks to the SCROLLPORT, which
            // starts under the padding — 16px of it would scroll away and let
            // ticket cards ride over the top of the bar.
            'fixed inset-0 z-50 space-y-3 overflow-y-auto overscroll-contain bg-page px-4 pb-4'
          : 'space-y-4'
      }
    >
      {/*
        Sticky in station mode. During a rush the board is several screens tall,
        and the way out of the mode — along with the station's own name and the
        connection state — must not be somewhere a cook has to scroll back to.
      */}
      <div
        className={cn(
          'flex flex-wrap items-center justify-between gap-3',
          focused && 'sticky top-0 z-10 -mx-4 border-b bg-page px-4 py-3',
        )}
      >
        <div className="flex flex-wrap items-center gap-3">
          {/*
            * The station is a SELECTOR, not a back button.
            *
            * It was a ghost "change station" link beside the name, which reads
            * as "go back" rather than "you are looking at one station of four".
            * The picker itself is an early return, so once a station was stored
            * it never rendered again — a cook on a quiet station saw an empty
            * board with no way to tell there was work waiting on another.
            */}
          <Select
            value={stationId ?? ''}
            onChange={(e) => setStationId(e.target.value || null)}
            className={cn('font-semibold', focused ? 'h-12 w-64 text-lg' : 'h-11 w-56 text-base')}
            aria-label={t('operations.kitchen.station')}
          >
            {stationRows.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
            <option value="">{t('operations.kitchen.changeStation')}</option>
          </Select>

          {focused && branchName && (
            <span className="text-lg text-ink-muted">{branchName}</span>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {/*
            The pill only exists in station mode, and it exists *because* of the
            mode: with the rail and the header gone there is nothing else on the
            screen that would look any different if this terminal had stopped
            receiving. It reports the one thing the app actually knows — realtime
            has given up, or it has not — and the banner below carries the retry.
          */}
          {focused && (
            <>
              <Badge tone={realtimeDown ? 'warning' : 'success'} className="text-sm">
                {t(realtimeDown ? 'kds.board.reconnecting' : 'kds.board.live')}
              </Badge>
              <span dir="ltr" className="text-lg tabular-nums text-ink-muted">
                {clock.format(now)}
              </span>
            </>
          )}

          {anyFetching && !initialLoading && (
            <span className="text-xs text-ink-muted">
              {t('operations.common.updating')}
            </span>
          )}
          <Button variant="outline" onClick={() => reload()} disabled={anyFetching}>
            <RefreshCw className="h-4 w-4" />
            {t('operations.common.refresh')}
          </Button>

          {/*
            One button, both directions, and it is never an icon on its own:
            the person who has to find the way out is a cook who did not set this
            screen up and has no reason to know Escape does anything.
          */}
          <Button
            variant={focused ? 'outline' : 'ghost'}
            aria-keyshortcuts={focused ? 'Escape' : undefined}
            onClick={focused ? focus.exit : focus.enter}
          >
            <Icon as={focused ? Minimize2 : Maximize2} />
            {focused
              ? t('operations.kitchen.focus.exit')
              : t('operations.kitchen.focus.enter')}
          </Button>
        </div>
      </div>

      {realtimeDown && (
        <div
          role="status"
          className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-amber-400 bg-amber-50 px-4 py-3 text-sm dark:border-amber-900 dark:bg-amber-950/40"
        >
          <span className="flex items-center gap-2 font-semibold text-amber-900 dark:text-amber-200">
            <TriangleAlert className="h-4 w-4 shrink-0" />
            {t('kds.board.reconnecting')}
          </span>
          <Button
            variant="outline"
            onClick={() => {
              reconnectRealtime();
              reload();
            }}
          >
            {t('common.retry')}
          </Button>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-4">
        {/*
          What used to be the two tabs. Counts, not switches: nothing here
          hides anything, and the same two badges appear on the cards below —
          so a barista glancing at "Takeaway · 2" can find both of them on the
          one board without changing what is on screen.
        */}
        <div className="flex flex-wrap items-center gap-2">
          <TicketKindBadge kind="DINE_IN" count={dineInCount} />
          <TicketKindBadge kind="TAKEAWAY" count={takeawayCount} />
          {/*
            The one number a station display exists to shout. Every breached
            ticket is already ringed in red on its own card, but on a board that
            scrolls the late one can be off screen — and in station mode the
            cards a cook can see are the only thing they will look at. Silent
            when nothing is late, so it never becomes furniture.
          */}
          {breachedCount > 0 && (
            <Badge tone="danger" className={cn('gap-1 whitespace-nowrap', dense.badge)}>
              <Icon as={TriangleAlert} size="xs" className="shrink-0" aria-hidden="true" />
              {`${t('kds.board.breached')} · ${breachedCount}`}
            </Badge>
          )}
        </div>

        {/* Cooking / pass / both — see `LANE_STATUSES`. */}
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs text-ink-muted">
            {t('operations.kitchen.laneLabel')}
          </span>
          {LANES.map((key) => (
            <button
              key={key}
              onClick={() => setLane(key)}
              className={cn(
                'rounded-lg border',
                dense.control,
                key === lane && 'border-accent bg-accent-soft text-accent-soft-fg',
              )}
            >
              {t(`operations.kitchen.lane.${key}`)}
            </button>
          ))}
        </div>
      </div>

      {/*
        Above the board, not inside it: these lines have no ticket and no
        station, so there is no column they could sit in. A cook pinned to one
        station never returns to the picker, so this is the only place they
        would ever see them.
      */}
      <UnroutedLane branchId={branchId} stations={stationRows} />

      {!!finishedTickets.length && (
        <div className="flex flex-wrap items-center gap-2 rounded-lg border bg-surface px-3 py-2 text-xs text-ink-muted">
          <span>
            {showFinished
              ? t('operations.kitchen.finishedShown', { count: finishedTickets.length })
              : t('operations.kitchen.finishedHidden', { count: finishedTickets.length })}
          </span>
          <Button size="sm" variant="ghost" onClick={() => setShowFinished((v) => !v)}>
            {showFinished
              ? t('operations.kitchen.hideFinished')
              : t('operations.kitchen.showFinished')}
          </Button>
        </div>
      )}

      {/*
        One queue failing is not the board failing. The banner names the queue
        that is missing and offers it its own retry, and the tickets that DID
        arrive stay on screen underneath — a full-page error here would hide
        live work, which is the whole of the bug this screen just fixed.
      */}
      {boardFailures.map((failure) => (
        <div
          key={failure.kind}
          className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-800 dark:bg-amber-950/50 dark:text-amber-300"
        >
          <span className="flex min-w-0 items-center gap-2">
            <TriangleAlert className="h-4 w-4 shrink-0" aria-hidden="true" />
            <span className="min-w-0 break-words">
              {t('operations.kitchen.boardFailed', {
                board: t(`order.fulfillment.${failure.kind}`),
                message: failure.message,
              })}
            </span>
          </span>
          <Button size="sm" variant="outline" onClick={() => void failure.retry()}>
            <RefreshCw className="h-4 w-4" />
            {t('common.retry')}
          </Button>
        </div>
      ))}

      {initialLoading ? (
        <LoadingState label={t('common.loading')} />
      ) : !visibleTickets.length ? (
        // Silence when both queues errored: the banners above have already said
        // what happened, and "nothing on the pass" would be a lie.
        boardFailures.length === 2 ? null : (
          <EmptyState
            title={
              finishedTickets.length
                ? t('operations.kitchen.allServed')
                : t('operations.kitchen.noTickets')
            }
            hint={
              finishedTickets.length
                ? t('operations.kitchen.allServedHint')
                : t('operations.kitchen.noTicketsHint')
            }
          />
        )
      ) : (
        // A KDS is usually a large landscape screen, and this board now carries
        // both queues — so it goes on using the width it is given. See `dense`
        // for what station mode does with the width the rail gives back.
        <div className={dense.grid}>
          {visibleTickets.map((ticket) => {
            const elapsed = elapsedOf(ticket, now);
            const sla = ticket.slaSeconds;
            const breached = isBreached(ticket, now);
            const progress = sla ? Math.min(100, Math.round((elapsed / sla) * 100)) : 0;
            const recallBlocked = recallBlockedReason(ticket);

            return (
              <Card
                key={ticket.id}
                className={breached ? 'border-red-400 ring-1 ring-red-300' : undefined}
              >
                <CardHeader className="gap-2">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <CardTitle className={cn('flex flex-wrap items-center gap-2', dense.title)}>
                        {ticket.number}
                        {/* Which queue this came from — see `TicketKindBadge`. */}
                        <TicketKindBadge kind={ticket.boardKind} />
                      </CardTitle>
                      <p className={cn('text-ink-muted', dense.meta)}>
                        {t('operations.kitchen.orderRef', { number: ticket.orderNumber })}
                        {ticket.customerName ? ` · ${ticket.customerName}` : ''}
                      </p>
                    </div>
                    <Badge tone={toneForStatus(ticket.status)} className={dense.badge}>
                      {t(`operations.ticketStatus.${ticket.status}`)}
                    </Badge>
                  </div>

                  <div className="flex items-center justify-between gap-2 text-sm">
                    <span
                      className={cn(
                        'tabular-nums',
                        dense.timer,
                        breached ? 'font-semibold text-red-600 dark:text-red-400' : 'font-medium',
                      )}
                      dir="ltr"
                    >
                      {formatDuration(elapsed)}
                    </span>
                    <span className={cn('text-ink-muted', dense.meta)}>
                      {sla
                        ? breached
                          ? t('operations.kitchen.slaBreached', {
                              duration: formatDuration(sla),
                            })
                          : t('operations.kitchen.slaTarget', { duration: formatDuration(sla) })
                        : t('operations.kitchen.noSla')}
                    </span>
                  </div>

                  {sla != null && (
                    <div className={cn('w-full overflow-hidden rounded-full bg-line', dense.bar)}>
                      <div
                        className={breached ? 'h-full bg-red-500' : 'h-full bg-accent-solid'}
                        style={{ width: `${Math.max(2, progress)}%` }}
                      />
                    </div>
                  )}

                  {ticket.scheduledFor && (
                    <p className={cn('text-ink-muted', dense.meta)}>
                      {t('operations.orders.scheduledFor', {
                        time: formatTime(ticket.scheduledFor, i18n.language),
                      })}
                    </p>
                  )}
                </CardHeader>

                <CardContent className="space-y-3">
                  {ticket.notes && (
                    <p
                      className={cn(
                        'rounded-lg bg-amber-50 p-2 text-amber-800 dark:bg-amber-950/50 dark:text-amber-300',
                        dense.sub,
                      )}
                    >
                      {ticket.notes}
                    </p>
                  )}

                  <ul className="space-y-2">
                    {ticket.items.map((item) => (
                      <li
                        key={item.id}
                        className="flex items-start justify-between gap-3 rounded-lg border p-2"
                      >
                        <div className="min-w-0">
                          <div className={cn('truncate font-medium', dense.item)}>
                            {formatQuantity(item.quantity)} × {item.name}
                          </div>
                          {!!item.modifiers?.length && (
                            <div className={cn('truncate text-ink-muted', dense.sub)}>
                              {item.modifiers.map((m) => `${m.name} ×${m.quantity}`).join(' · ')}
                            </div>
                          )}
                          {item.notes && (
                            <div className={cn('text-amber-700 dark:text-amber-400', dense.sub)}>
                              {item.notes}
                            </div>
                          )}
                          <Badge tone={toneForStatus(item.status)} className={cn('mt-1', dense.badge)}>
                            {t(`operations.itemStatus.${item.status}`)}
                          </Badge>
                        </div>

                        <KitchenItemActions
                          item={item}
                          busy={busy === `item:${item.id}`}
                          can={can}
                          onRun={(action) => runAction(item, action)}
                        />
                      </li>
                    ))}
                  </ul>

                  <div className="flex flex-wrap gap-2">
                    {can('kitchen.prioritize') && (
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={
                          busy === `priority:${ticket.id}` ||
                          ticket.priority >= MAX_TICKET_PRIORITY
                        }
                        title={
                          ticket.priority >= MAX_TICKET_PRIORITY
                            ? t('operations.kitchen.priorityAtMax', { max: MAX_TICKET_PRIORITY })
                            : undefined
                        }
                        onClick={() => raisePriority(ticket)}
                      >
                        {t('operations.kitchen.raisePriority', { priority: ticket.priority })}
                      </Button>
                    )}

                    {can('kitchen.recall') && (
                      <Button
                        size="sm"
                        variant="ghost"
                        disabled={!!recallBlocked}
                        title={
                          recallBlocked
                            ? t(`operations.kitchen.recallBlocked.${recallBlocked}`)
                            : undefined
                        }
                        onClick={() => setRecallTicket(ticket)}
                      >
                        <Undo2 className="h-4 w-4 rtl:-scale-x-100" />
                        {t('operations.kitchen.recall')}
                      </Button>
                    )}
                  </div>

                  {/*
                    `notReady` needs no words — the ticket's own status badge is
                    two lines up. `allPickedUp` is the surprising refusal: the
                    ticket reads READY and Recall still will not fire, so that
                    one gets spelled out rather than left to a hover.
                  */}
                  {can('kitchen.recall') && recallBlocked === 'allPickedUp' && (
                    <p className="text-xs text-ink-muted">
                      {t('operations.kitchen.recallBlocked.allPickedUp')}
                    </p>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <Dialog
        open={!!recallTicket}
        onClose={() => {
          setRecallTicket(null);
          setRecallReason('');
        }}
        title={t('operations.kitchen.recallTitle')}
        description={t('operations.kitchen.recallHint')}
        footer={
          <>
            <Button
              variant="ghost"
              onClick={() => {
                setRecallTicket(null);
                setRecallReason('');
              }}
            >
              {t('common.cancel')}
            </Button>
            {/*
              Realtime keeps patching item statuses under an open dialog, so a
              waiter picking the last line up while the reason is being typed
              would turn this into a guaranteed 400. Re-read the live row.
            */}
            <Button onClick={submitRecall} disabled={!!busy || !!liveRecallBlocked}>
              {t('common.confirm')}
            </Button>
          </>
        }
      >
        {liveRecallBlocked && (
          <p className="mb-2 rounded-lg bg-amber-50 dark:bg-amber-950/50 p-2 text-xs text-amber-800 dark:text-amber-300">
            {t(`operations.kitchen.recallBlocked.${liveRecallBlocked}`)}
          </p>
        )}
        <Field label={t('operations.common.reason')}>
          <Textarea
            rows={3}
            value={recallReason}
            onChange={(e) => setRecallReason(e.target.value)}
          />
        </Field>
      </Dialog>
    </div>
  );
}
